import React, { useMemo } from 'react';
import { Box, FormControl, Select, MenuItem, Typography, TextField } from '@mui/material';
import { scheduleForSave } from '../../services/functionTriggers';
import { describeClockGap, findClockGap } from '../../utils/scheduleTimes';

const TIMEZONES = [
  'UTC',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'US/Eastern',
  'US/Central',
  'US/Mountain',
  'US/Pacific',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Australia/Sydney',
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

// `disabled` reaches every control: a caller locking the schedule (a View-managed
// automation) must not rely on styling alone, which leaves keyboard input live.
function TriggerScheduleConfig({ value, onChange, disabled = false }) {
  const config = value || {
    frequency: 'daily',
    day_of_week: 0,
    day_of_month: 1,
    hour: 9,
    minute: 0,
    timezone: 'Europe/London',
  };

  const handleChange = (field, val) => {
    onChange(scheduleForSave({ ...config, [field]: val }));
  };

  // A wall time the clocks skip on some day of the coming year is named, with
  // the time the run happens at instead (M202).
  const { frequency, hour, minute, timezone, day_of_week: dayOfWeek, day_of_month: dayOfMonth } = config;
  const clockGap = useMemo(
    () => findClockGap({ frequency, hour, minute, timezone, day_of_week: dayOfWeek, day_of_month: dayOfMonth }),
    [frequency, hour, minute, timezone, dayOfWeek, dayOfMonth],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <Typography variant="caption" color="text.secondary">Frequency</Typography>
        <FormControl fullWidth size="small">
          <Select
            value={config.frequency}
            disabled={disabled}
            onChange={(e) => handleChange('frequency', e.target.value)}
          >
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {config.frequency === 'weekly' && (
        <Box>
          <Typography variant="caption" color="text.secondary">Day of Week</Typography>
          <FormControl fullWidth size="small">
            <Select
              value={config.day_of_week ?? 0}
              disabled={disabled}
              onChange={(e) => handleChange('day_of_week', e.target.value)}
            >
              {DAYS_OF_WEEK.map(d => (
                <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {config.frequency === 'monthly' && (
        <Box>
          <Typography variant="caption" color="text.secondary">Day of Month</Typography>
          <FormControl fullWidth size="small">
            <Select
              value={config.day_of_month ?? 1}
              disabled={disabled}
              onChange={(e) => handleChange('day_of_month', e.target.value)}
            >
              {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">Hour</Typography>
          <FormControl fullWidth size="small">
            <Select
              value={config.hour ?? 9}
              disabled={disabled}
              onChange={(e) => handleChange('hour', e.target.value)}
            >
              {Array.from({ length: 24 }, (_, i) => (
                <MenuItem key={i} value={i}>{String(i).padStart(2, '0')}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">Minute</Typography>
          <FormControl fullWidth size="small">
            <Select
              value={config.minute ?? 0}
              disabled={disabled}
              onChange={(e) => handleChange('minute', e.target.value)}
            >
              {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(m => (
                <MenuItem key={m} value={m}>{String(m).padStart(2, '0')}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box>
        <Typography variant="caption" color="text.secondary">Timezone</Typography>
        <FormControl fullWidth size="small">
          <Select
            value={config.timezone || 'Europe/London'}
            disabled={disabled}
            onChange={(e) => handleChange('timezone', e.target.value)}
          >
            {TIMEZONES.map(tz => (
              <MenuItem key={tz} value={tz}>{tz}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {clockGap && (
        <Typography variant="caption" sx={{ color: 'warning.main' }}>
          {describeClockGap(clockGap)}
        </Typography>
      )}
    </Box>
  );
}

export default TriggerScheduleConfig;
